import { Component, ChangeDetectionStrategy, ViewEncapsulation, ChangeDetectorRef, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CvdChartDataValue, CvdChartData, BoxPlotTooltip, BoxPlotEvent, BoxplotData} from '@covance/ng-cvd3';
import { BaseOsrChart } from './base-osr-chart';
import { UrlBuilder } from '../../core/url-builder/url-builder';
import { find, groupBy, chain, map, orderBy  } from 'lodash';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { TooltipItem } from '../../shared/tooltip-item';
import { TooltipFormatter } from '../../shared/tooltip-formatter';
import { ChartLegendItem } from '../../legend';
import { getShapeByGender, getColorByGender, buildBoxPlotGenderLegend, clearOutliers } from '../../charts/chart-helper';
import { findSelectedSite } from '../../shared/route-helpers';
import { SITE_LAB_RESULTS_STORAGE, SiteTestDataStorage } from './site-test-data-storage';

const showOnlyPointsMaxDataLength = 3;

@Component({
    selector: 'lab-boxplot-chart',
    template: require('./lab-boxplot-chart.component.html'),
    styles: [require('./lab-chart.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LabBoxplotChartComponent extends BaseOsrChart {

    public showSplitGenders = true;
    public sexLegendData: ChartLegendItem[];
    public xAxisTickFormat: (visitCode: string) => string;

    constructor(
        @Inject(SITE_LAB_RESULTS_STORAGE) filteredStorage: SiteTestDataStorage,
        changeDetectorRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private route: ActivatedRoute,
        private tooltipFormatter: TooltipFormatter
    ) {
        super(filteredStorage, changeDetectorRef);
        this.xAxisTickFormat = visitCode => {
            const visitInfo = find(this.visitsInfo, v => v.visitCode === visitCode);
            return visitInfo && visitInfo.visitTitle ? visitInfo.visitTitle : '';
        };
    }

    public isBoxVisible = (d: CvdChartDataValue) => showOnlyPointsMaxDataLength < (d.y as BoxplotData[]).length;

    public calcWhiskers = (d: BoxplotData []) =>
        showOnlyPointsMaxDataLength < d.length ? [0, d.length - 1] : [-1, -1]

    public tooltip = (data: BoxPlotTooltip) => {
        const units = this.yAxisName.unit || '';
        const tooltipItems: TooltipItem[] = [];

        if (data.isBox) {
            tooltipItems.push(new TooltipItem('Gender', data.key));
            tooltipItems.push(new TooltipItem('Min', `${data.whiskers[0]} ${units}`));
            tooltipItems.push(new TooltipItem('Max', `${data.whiskers[1]} ${units}`));
            tooltipItems.push(new TooltipItem('Median', `${data.quartiles[1]} ${units}`));
            tooltipItems.push(new TooltipItem('First Quartile', `${data.quartiles[0]} ${units}`));
            tooltipItems.push(new TooltipItem('Third Quartile', `${data.quartiles[2]} ${units}`));

            this.tooltipFormatter.clear();
            this.tooltipFormatter.addTooltipItems(tooltipItems);
            return this.tooltipFormatter.getTooltipContent();
        }

        const labTestId = data.data.rows[0];
        const labTestResult = find(this.data, l => l.labTestId === labTestId);
        const subjectTitle = SubjectTitleRenderer.renderTitle({ data: labTestResult });
        tooltipItems.push(new TooltipItem('Value', `${labTestResult.currentResult.numericResult} ${units}`));
        tooltipItems.push(new TooltipItem('Subject', subjectTitle));
        this.tooltipFormatter.clear();
        this.tooltipFormatter.addTooltipItems(tooltipItems);
        return this.tooltipFormatter.getTooltipContent();
    }

    public onSplitToggle() {
        clearOutliers();
        this.lineChartData = this.buildChartData();
        buildBoxPlotGenderLegend(this.lineChartData, this.showSplitGenders);
    }

    public goToSubject(event: BoxPlotEvent) {
        if (event.isBox) {
            return;
        }

        const labTestId = event.data.rows[0];
        const labTestResult = find(this.data, l => l.labTestId === labTestId);

        if (!labTestResult.zcSubjectId) {
            return;
        }

        this.urlBuilder
            .esa()
            .site(findSelectedSite(this.route.snapshot))
            .subject({ zcSubjectId: labTestResult.zcSubjectId })
            .navigate();
    }

    protected buildChartData() {
        const validData = this.currentTestData.filter(d => !d.isCancelled);
        const dataSource = this.showSplitGenders ? groupBy(validData, test => test.sex) : {'All Genders': validData};

        const dataByGender = map(dataSource, (genderData, gender) => {
            const byVisits = chain(genderData)
                .groupBy(test => test.visitCode).value();

            const visitData = chain(this.visitsInfo)
                .filter(visitInfo => !!visitInfo.visitCode)
                .map(visitInfo => {
                    const yValues = byVisits[visitInfo.visitCode] || [];

                    return {
                        x: visitInfo.visitCode,
                        y: yValues.map(test => ({
                            y: test.currentResult.numericResult,
                            rowId: test.labTestId,
                            shape: getShapeByGender(test.sex)
                        })),
                        rows: [],
                        series: 0
                    } as CvdChartDataValue;
                })
                .sortBy(value => value.x)
                .value();

            const data: CvdChartData<CvdChartDataValue> = {
                key: gender,
                rows: [],
                color: getColorByGender(gender),
                values: visitData
            };
            return data;
        });
        return orderBy(dataByGender, (d: CvdChartData<CvdChartDataValue>) => d.key);
    }

    protected chartSpecific() {
        // we need this because cvd3 has a bug in rendering outliers. it doesn't remove all old outliers'
        //TODO: remove this method when cvd3 bug is fixed;
        clearOutliers();
        buildBoxPlotGenderLegend(this.lineChartData, this.showSplitGenders);
    }
}
